// LiveStream.jsx — Multi-slot live streaming (up to 3 concurrent streamers)
//
// Viewer:   /live            — watch all active streams + chat
// Streamer: /live?stream=1   — claim a slot and go live

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import GameRoom from './GameRoom';
import NetplayRoom from './NetplayRoom';
import GbaLinkedPage from './GbaLinkedPage';
import LiveVoiceControls from './LiveVoiceControls';
import LiveGuestPresence from './LiveGuestPresence';
import { useLiveGuestPresence } from './useLiveGuestPresence';
import { liveModes } from './liveModes';

const SERVER = process.env.REACT_APP_SERVER_URL || '';
const MAX_SLOTS = 3;

const C = {
  bg: '#0E0E10', sidebar: '#18181B', card: '#1F1F23', border: '#2A2A2E',
  text: '#EFEFF1', muted: '#ADADB8', accent: '#9147FF', gold: '#C8922A',
  live: '#EB0400', input: '#3A3A3D',
};

// Slot colors for multi-stream labels
const SLOT_COLORS = ['#9147FF', '#22C55E', '#F59E0B'];

import Hls from 'hls.js';

function useIsMobile() {
  const [m, setM] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

function NameGate({ onEnter }) {
  const [name, setName] = useState('');
  const submit = () => name.trim() && onEnter(name.trim());
  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '40px 36px', width: 'min(400px,90vw)', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🍺</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 6 }}>Join the Stream</div>
        <div style={{ color: C.muted, fontSize: 14, marginBottom: 28 }}>Enter a name to watch, chat, or play together</div>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder="Your name..." maxLength={32}
          style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: C.input, color: C.text, fontSize: 15, outline: 'none', boxSizing: 'border-box', marginBottom: 14 }}/>
        <button onClick={submit} style={{ width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', background: C.gold, color: '#0E0E10', fontWeight: 800, fontSize: 16, cursor: 'pointer' }}>
          Enter Live Room
        </button>
      </div>
    </div>
  );
}

const Chat = React.memo(function Chat({ messages, onSend, displayName, guests }) {
  const [msg, setMsg] = useState('');
  const endRef = useRef();
  const prevLen = useRef(0);
  useEffect(() => {
    if (messages.length !== prevLen.current) { prevLen.current = messages.length; endRef.current?.scrollIntoView({ behavior: 'smooth' }); }
  }, [messages]);
  const send = useCallback(() => { const t = msg.trim(); if (!t) return; onSend(t); setMsg(''); }, [msg, onSend]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: C.sidebar }}>
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.border}`, fontWeight: 700, color: C.text, fontSize: 14, flexShrink: 0 }}>💬 Stream Chat</div>
      <LiveGuestPresence guests={guests}/>
      <div style={{ flex: 1, overflow: 'auto', padding: '8px 12px' }}>
        {messages.length === 0
          ? <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Be the first to chat!</div>
          : messages.map((m, i) => (
            <div key={m.id || i} style={{ marginBottom: 6, lineHeight: 1.5 }}>
              <span style={{ fontWeight: 700, color: m.role === 'streamer' ? C.gold : C.accent, fontSize: 13 }}>{m.role === 'streamer' && '🎮 '}{m.display_name}</span>
              <span style={{ color: C.muted, fontSize: 12 }}>: </span>
              <span style={{ color: C.text, fontSize: 13 }}>{m.content}</span>
            </div>
          ))
        }
        <div ref={endRef}/>
      </div>
      <div style={{ padding: '10px 12px', borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={msg} onChange={e => setMsg(e.target.value.slice(0, 500))} onKeyDown={e => e.key === 'Enter' && send()}
            placeholder={`Chat as ${displayName}...`}
            style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.input, color: C.text, fontSize: 13, outline: 'none' }}/>
          <button onClick={send} style={{ padding: '9px 14px', borderRadius: 8, border: 'none', background: C.gold, color: '#0E0E10', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>Chat</button>
        </div>
      </div>
    </div>
  );
});

const HlsPlayer = React.memo(function HlsPlayer({ hlsUrl }) {
  const videoRef = useRef();
  const hlsRef = useRef();
  const retryRef = useRef();
  const startHls = useCallback((url) => {
    if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    clearTimeout(retryRef.current);
    if (!videoRef.current) return;
    let mediaErrorRecoveries = 0;
    const hls = new Hls({
      lowLatencyMode: true, liveSyncDurationCount: 1, liveMaxLatencyDurationCount: 2,
      liveDurationInfinity: true, highBufferWatchdogPeriod: 0.5, nudgeMaxRetry: 10, nudgeOffset: 0.2,
      maxLiveSyncPlaybackRate: 2, manifestLoadingTimeOut: 5000, manifestLoadingMaxRetry: 30,
      manifestLoadingRetryDelay: 500, levelLoadingTimeOut: 5000, fragLoadingTimeOut: 8000,
      startPosition: -1, backBufferLength: 0, xhrSetup: xhr => xhr.setRequestHeader('Cache-Control', 'no-cache'),
    });
    hlsRef.current = hls;
    hls.loadSource(url); hls.attachMedia(videoRef.current);
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      const v = videoRef.current; if (!v) return;
      if (hls.liveSyncPosition) v.currentTime = hls.liveSyncPosition;
      v.play().catch(() => { v.muted = true; v.play().catch(() => {}); });
    });
    hls.on(Hls.Events.FRAG_CHANGED, () => {
      const v = videoRef.current; const edge = hls.liveSyncPosition;
      if (v && edge && !v.paused && v.currentTime < edge - 3) v.currentTime = edge;
    });
    hls.on(Hls.Events.ERROR, (_, data) => {
      if (!data.fatal) return;
      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        hls.stopLoad();
        retryRef.current = setTimeout(() => { if (hlsRef.current === hls) hls.startLoad(); }, 1000);
      } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
        // After 3 failed recoveries give up and do a full restart — avoids
        // the infinite recoverMediaError loop seen in buffer-append errors.
        if (++mediaErrorRecoveries <= 3) {
          hls.recoverMediaError();
        } else {
          retryRef.current = setTimeout(() => { if (hlsRef.current === hls) startHls(url); }, 2000);
        }
      } else {
        retryRef.current = setTimeout(() => startHls(url), 2000);
      }
    });
  }, []); // eslint-disable-line
  useEffect(() => {
    if (!hlsUrl) return;
    const fullUrl = `${SERVER}${hlsUrl}`;
    let stallTimer, onVisible;
    if (!Hls.isSupported()) return; // browser has native HLS (Safari) — let the video element handle it
    startHls(fullUrl);
    let lastTime = -1, stallCount = 0;
    stallTimer = setInterval(() => {
      const v = videoRef.current; if (!v || v.paused || v.ended) return;
      // Only restart on a genuine prolonged stall (15s = 5 × 3s ticks).
      // A shorter threshold causes false restarts during HLS buffer recovery,
      // which creates a new hls_ctx mid-stream and resets playback to the start.
      if (v.currentTime === lastTime) { if (++stallCount >= 5) { stallCount = 0; startHls(fullUrl); } } else { stallCount = 0; }
      lastTime = v.currentTime;
    }, 3000);
    onVisible = () => {
      if (document.visibilityState !== 'visible' || !hlsRef.current) return;
      const edge = hlsRef.current.liveSyncPosition; const v = videoRef.current;
      if (edge && v && v.currentTime < edge - 3) { v.currentTime = edge; v.play().catch(() => {}); }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(stallTimer); clearTimeout(retryRef.current);
      if (onVisible) document.removeEventListener('visibilitychange', onVisible);
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
  }, [hlsUrl, startHls]);
  return <video ref={videoRef} autoPlay playsInline controls style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}/>;
});

const WebRTCPlayer = React.memo(function WebRTCPlayer({ stream }) {
  const videoRef = useRef();
  useEffect(() => {
    const v = videoRef.current; if (!v || !stream) return;
    if (v.srcObject !== stream) { v.srcObject = stream; v.play().catch(() => { v.muted = true; v.play().catch(() => {}); }); }
  }, [stream]);
  return <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}/>;
});

function OfflinePlaceholder() {
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: '#000' }}>
      <div style={{ fontSize: 52 }}>🍺</div>
      <div style={{ color: C.text, fontSize: 20, fontWeight: 700 }}>Stream is Offline</div>
      <div style={{ color: C.muted, fontSize: 14 }}>Check back later</div>
    </div>
  );
}

function ConnectingPlaceholder() {
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, background: '#000' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width: 40, height: 40, border: '3px solid #333', borderTopColor: C.gold, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/>
      <div style={{ color: C.muted, fontSize: 14 }}>Connecting to stream...</div>
    </div>
  );
}

// ── Layout system ─────────────────────────────────────────────────────────────
// Layouts: equal | focus | spotlight | grid | pip
// focus:     one slot ~65%, others ~35% stacked on the right
// spotlight: one slot 100%, others hidden (click name to switch)
// grid:      2-col grid
// pip:       own stream as small overlay in corner of focused slot

const LAYOUT_OPTS = [
  { id: 'equal',     label: '⬜ Equal',      tip: 'All streams same size' },
  { id: 'focus',     label: '⬛ Focus',      tip: 'One big + others small' },
  { id: 'spotlight', label: '🔲 Spotlight',  tip: 'One stream fills everything' },
  { id: 'grid',      label: '⊞ Grid',        tip: '2-column grid' },
  { id: 'pip',       label: '▣ PiP',         tip: 'Your stream as corner overlay' },
];

function useFullscreen(ref) {
  const [isFs, setIsFs] = useState(false);
  useEffect(() => {
    const h = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);
  const enter = useCallback(() => { ref.current?.requestFullscreen?.().catch(() => {}); }, [ref]);
  const exit  = useCallback(() => { document.exitFullscreen?.().catch(() => {}); }, []);
  return { isFs, enter, exit };
}

// Single stream panel with overlay controls
function StreamPanel({ slotStatus, remoteStream, localStream, isMySlot, slotIndex, totalSlots,
                       onHide, onFullscreen, isFullscreen, showControls, isPip }) {
  const [hovered, setHovered] = useState(false);
  const color = SLOT_COLORS[slotIndex % SLOT_COLORS.length];

  const videoEl = slotStatus.source === 'rtmp' && slotStatus.hlsUrl
    ? <HlsPlayer hlsUrl={slotStatus.hlsUrl}/>
    : (remoteStream || (isMySlot && localStream))
      ? <WebRTCPlayer stream={isMySlot ? localStream : remoteStream}/>
      : <ConnectingPlaceholder/>;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative', background: '#000', width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {videoEl}

      {/* Name badge */}
      <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 5, alignItems: 'center', pointerEvents: 'none' }}>
        <div style={{ background: C.live, color: '#fff', fontWeight: 800, fontSize: isPip ? 8 : 10, padding: '2px 5px', borderRadius: 4, letterSpacing: 1 }}>LIVE</div>
        {(totalSlots > 1 || isMySlot) && (
          <div style={{ background: color + 'cc', color: '#fff', fontWeight: 700, fontSize: isPip ? 9 : 11, padding: '2px 7px', borderRadius: 4, backdropFilter: 'blur(4px)' }}>
            {slotStatus.streamerName}{isMySlot ? ' (you)' : ''}
          </div>
        )}
      </div>

      {/* Hover overlay controls */}
      {showControls && (hovered || isFullscreen) && !isPip && (
        <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6 }}>
          {onHide && (
            <button onClick={onHide} title="Hide this stream"
              style={{ padding: '5px 9px', borderRadius: 7, border: 'none', background: 'rgba(0,0,0,0.7)', color: '#fff', cursor: 'pointer', fontSize: 13, backdropFilter: 'blur(4px)' }}>
              🙈
            </button>
          )}
          <button onClick={onFullscreen} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            style={{ padding: '5px 9px', borderRadius: 7, border: 'none', background: 'rgba(0,0,0,0.7)', color: '#fff', cursor: 'pointer', fontSize: 13, backdropFilter: 'blur(4px)' }}>
            {isFullscreen ? '⤓' : '⤢'}
          </button>
        </div>
      )}
    </div>
  );
}

// Streamer controls — per-slot
const StreamerControls = React.memo(function StreamerControls({ onStream, onEnd, streaming, localStream, mySlot, slotTitle, onTitleChange, user, onCameraFlip }) {
  const [source, setSource]         = useState('screen');
  const [editTitle, setEditTitle]   = useState(slotTitle || '');
  const [audioLevel, setAudioLevel] = useState(0);
  const [selAudio, setSelAudio]     = useState('');
  const [selVideo, setSelVideo]     = useState('');
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [facingMode, setFacingMode] = useState('user');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [obsKey, setObsKey]         = useState(null);   // { stream_key, rtmp_url, srt_url }
  const [keyLoading, setKeyLoading] = useState(false);
  const previewRef   = useRef();
  const meterCleanup = useRef(null);
  const isIOS = /iP(hone|ad|od)/i.test(navigator.userAgent);

  // Fetch personal stream key when OBS tab is selected
  useEffect(() => {
    if (source !== 'obs' || obsKey) return;
    setKeyLoading(true);
    const token = localStorage.getItem('tavern_token') || '';
    fetch(`${SERVER}/stream/my-key`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.stream_key) setObsKey(d); })
      .catch(() => {})
      .finally(() => setKeyLoading(false));
  }, [source, obsKey]);

  const regenerateKey = async () => {
    if (!window.confirm('Generate a new stream key? Your old OBS/SRT settings will stop working.')) return;
    setKeyLoading(true);
    const token = localStorage.getItem('tavern_token') || '';
    fetch(`${SERVER}/stream/regenerate-key`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.stream_key) setObsKey(d); })
      .catch(() => {})
      .finally(() => setKeyLoading(false));
  };

  useEffect(() => {
    const load = async () => {
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        setAudioDevices(all.filter(d => d.kind === 'audioinput'));
        const cams = all.filter(d => d.kind === 'videoinput');
        setVideoDevices(cams); setHasMultipleCameras(cams.length > 1);
      } catch {}
    };
    load();
    navigator.mediaDevices.addEventListener('devicechange', load);
    return () => navigator.mediaDevices.removeEventListener('devicechange', load);
  }, []);

  useEffect(() => {
    if (previewRef.current && localStream) { previewRef.current.srcObject = localStream; previewRef.current.play().catch(() => {}); }
    if (meterCleanup.current) { meterCleanup.current(); meterCleanup.current = null; }
    const tracks = localStream?.getAudioTracks() || [];
    if (!tracks.length) { setAudioLevel(0); return; }
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ctx.createAnalyser(); analyser.fftSize = 256;
      ctx.createMediaStreamSource(localStream).connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount); let alive = true;
      const tick = () => { if (!alive) return; analyser.getByteFrequencyData(data); setAudioLevel(Math.min(100, (data.reduce((a, b) => a + b, 0) / data.length) * 2)); requestAnimationFrame(tick); };
      tick(); meterCleanup.current = () => { alive = false; ctx.close().catch(() => {}); };
    } catch {}
    return () => { if (meterCleanup.current) { meterCleanup.current(); meterCleanup.current = null; } };
  }, [localStream]);

  const flipCamera = async () => {
    if (!localStream) return;
    const next = facingMode === 'user' ? 'environment' : 'user';
    try {
      const ns = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: next } }, audio: false });
      const newTrack = ns.getVideoTracks()[0]; if (!newTrack) return;
      const old = localStream.getVideoTracks()[0];
      if (old) { old.stop(); localStream.removeTrack(old); }
      localStream.addTrack(newTrack);
      if (previewRef.current) { previewRef.current.srcObject = null; previewRef.current.srcObject = localStream; previewRef.current.play().catch(() => {}); }
      setFacingMode(next);
      // Notify parent so it can replaceTrack on all active peer connections —
      // without this viewers lose the video track and their WebRTC PC fails.
      if (onCameraFlip) onCameraFlip(newTrack);
    } catch (e) { console.error('flip:', e); }
  };

  const startCapture = async () => {
    const audio = selAudio ? { deviceId: { exact: selAudio }, echoCancellation: false, noiseSuppression: false, sampleRate: 48000 } : { echoCancellation: false, noiseSuppression: false, sampleRate: 48000 };
    const video = selVideo ? { deviceId: { exact: selVideo }, width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } } : { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } };
    try {
      const stream = source === 'screen'
        ? await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: 30, max: 60 }, cursor: 'always' }, audio })
        : await navigator.mediaDevices.getUserMedia({ video, audio });
      onStream(stream);
    } catch (e) { if (e.name !== 'AbortError' && e.name !== 'NotAllowedError') alert('Could not capture: ' + e.message); }
  };

  const updateTitle = () => { const t = editTitle.trim(); if (t) onTitleChange(t); };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: C.text }}>
          🎮 Stream Controls {mySlot ? <span style={{ color: SLOT_COLORS[(mySlot - 1) % SLOT_COLORS.length], fontSize: 12 }}>· Slot {mySlot}</span> : ''}
        </div>
        {streaming && <div style={{ background: C.live, color: '#fff', fontWeight: 800, fontSize: 11, padding: '4px 10px', borderRadius: 6, letterSpacing: 1 }}>● LIVE</div>}
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>Stream Title</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={editTitle} onChange={e => setEditTitle(e.target.value)} onKeyDown={e => e.key === 'Enter' && updateTitle()}
            placeholder="Stream title..." maxLength={80}
            style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.input, color: C.text, fontSize: 13, outline: 'none' }}/>
          <button onClick={updateTitle} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: `${C.gold}22`, color: C.gold, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>Set</button>
        </div>
      </div>
      {!streaming && (
        <>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Stream Source</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['screen','🖥 Screen'],['camera','📷 Camera'],['obs','🎬 OBS/SRT']].map(([v, label]) => (
                <button key={v} onClick={() => setSource(v)}
                  style={{ flex: 1, padding: '8px 4px', borderRadius: 8, border: `1px solid ${source === v ? C.gold : C.border}`, background: source === v ? `${C.gold}22` : 'transparent', color: source === v ? C.gold : C.muted, cursor: 'pointer', fontSize: 12, fontWeight: source === v ? 700 : 400 }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          {source !== 'obs' && audioDevices.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>🎤 Microphone</div>
              <select value={selAudio} onChange={e => setSelAudio(e.target.value)} style={{ width: '100%', background: '#0D0F14', border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '8px 10px', fontSize: 12, outline: 'none' }}>
                <option value="">— System Default —</option>
                {audioDevices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${d.deviceId.slice(0,6)}`}</option>)}
              </select>
            </div>
          )}
          {source === 'camera' && videoDevices.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 5 }}>📷 Camera</div>
              <select value={selVideo} onChange={e => setSelVideo(e.target.value)} style={{ width: '100%', background: '#0D0F14', border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '8px 10px', fontSize: 12, outline: 'none' }}>
                <option value="">— System Default —</option>
                {videoDevices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.slice(0,6)}`}</option>)}
              </select>
            </div>
          )}
          {source === 'obs' && (
            <div style={{ background: '#0D0F14', border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.gold, flex: 1 }}>🎬 Your OBS Settings</div>
                <button onClick={regenerateKey} disabled={keyLoading}
                  style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, cursor: 'pointer' }}
                  title="Generate a new stream key (invalidates old one)">
                  {keyLoading ? '…' : '🔄 New Key'}
                </button>
              </div>
              {keyLoading && !obsKey
                ? <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', padding: '10px 0' }}>Loading your stream key…</div>
                : obsKey ? (
                  <>
                    {/* Stream Key */}
                    <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>🔑 Your Stream Key <span style={{ color: '#EF4444', fontSize: 11 }}>(keep private)</span></div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
                      <code style={{ flex: 1, background: '#0A0C10', padding: '6px 10px', borderRadius: 6, fontSize: 11, color: '#22C55E', wordBreak: 'break-all' }}>{obsKey.stream_key}</code>
                      <button onClick={() => navigator.clipboard?.writeText(obsKey.stream_key)} style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap' }}>Copy</button>
                    </div>
                    {/* RTMP */}
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>RTMP (stable)</div>
                    <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Settings → Stream → Custom RTMP → paste URL, then set Key above</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
                      <code style={{ flex: 1, background: '#0A0C10', padding: '6px 10px', borderRadius: 6, fontSize: 11, color: '#22C55E', wordBreak: 'break-all' }}>{obsKey.rtmp_url}</code>
                      <button onClick={() => navigator.clipboard?.writeText(obsKey.rtmp_url)} style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap' }}>Copy</button>
                    </div>
                    {/* SRT */}
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.gold, marginBottom: 4 }}>⚡ SRT (low latency)</div>
                    <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Settings → Stream → Custom — paste full URL, leave key blank</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                      <code style={{ flex: 1, background: '#0A0C10', padding: '6px 10px', borderRadius: 6, fontSize: 11, color: C.gold, wordBreak: 'break-all' }}>{obsKey.srt_url}</code>
                      <button onClick={() => navigator.clipboard?.writeText(obsKey.srt_url)} style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap' }}>Copy</button>
                    </div>
                    <div style={{ fontSize: 12, color: '#F59E0B' }}>Hit Start Streaming in OBS — goes live automatically in your slot.</div>
                  </>
                ) : (
                  <div style={{ fontSize: 12, color: '#EF4444' }}>Could not load stream key — make sure you're logged in.</div>
                )
              }
            </div>
          )}
        </>
      )}
      {localStream && (
        <div style={{ marginBottom: 14, borderRadius: 10, overflow: 'hidden', background: '#000', position: 'relative' }}>
          <video ref={previewRef} autoPlay playsInline muted style={{ width: '100%', maxHeight: 180, objectFit: 'contain', display: 'block' }}/>
          {streaming && source === 'camera' && (hasMultipleCameras || isIOS) && (
            <button onClick={flipCamera} style={{ position: 'absolute', top: 8, right: 8, width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🔄</button>
          )}
          <div style={{ padding: '6px 10px', background: '#0A0C10' }}>
            <div style={{ fontSize: 11, color: audioLevel > 5 ? '#22C55E' : '#4B5563', marginBottom: 3 }}>🎙 {audioLevel > 5 ? 'Audio detected' : 'Audio silent'}</div>
            <div style={{ height: 5, background: '#1A1D24', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 3, transition: 'width 0.05s ease', width: `${audioLevel}%`, background: audioLevel > 70 ? '#EF4444' : audioLevel > 40 ? '#F59E0B' : '#22C55E' }}/>
            </div>
          </div>
        </div>
      )}
      {source !== 'obs' && (
        <button onClick={streaming ? onEnd : startCapture}
          style={{ width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', background: streaming ? '#EF4444' : C.gold, color: streaming ? '#fff' : '#0E0E10', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}>
          {streaming ? '⬛ End Stream' : '🔴 Go Live'}
        </button>
      )}
      {source === 'obs' && streaming && (
        <button onClick={onEnd} style={{ width: '100%', padding: '13px 0', borderRadius: 10, border: 'none', background: '#EF4444', color: '#fff', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}>⬛ End Stream</button>
      )}
    </div>
  );
});

// ── Main component ────────────────────────────────────────────────────────────
export default function LiveStream({ user }) {
  const params     = new URLSearchParams(window.location.search);
  const isStreamer = params.has('stream') && !!user;
  const isMobile   = useIsMobile();
  const { localNetplay } = liveModes(window.location.search);
  const nativeLinked = params.get('games') === 'gba-link';
  const [dualGames, setDualGames] = useState(() => params.get('screens') === '2');
  const [activeGame, setActiveGame] = useState(0);
  const [viewMode, setViewMode] = useState(() => liveModes(window.location.search).viewMode);

  const [displayName, setDisplayName]   = useState(() => {
    if (user?.username) return user.username;
    try { return sessionStorage.getItem('tavern-live-guest-name') || null; } catch { return null; }
  });
  const [messages, setMessages]         = useState([]);
  // status.slots = array of { slot, live, source, hlsUrl, streamerName, title, ... }
  const [status, setStatus]             = useState({ live: false, slots: [], viewerCount: 0 });
  // remoteStreams: Map slotNum → MediaStream
  const [remoteStreams, setRemoteStreams] = useState({});
  const [localStream, setLocalStream]   = useState(null);
  const [streaming, setStreaming]       = useState(false);
  const [mySlot, setMySlot]             = useState(null);
  const [mySlotTitle, setMySlotTitle]   = useState('Live Stream');

  // Layout
  const [layout, setLayout]             = useState('equal');   // equal|focus|spotlight|grid|pip
  const [spotlightSlot, setSpotlightSlot] = useState(null);   // which slot is spotlighted
  const [hiddenSlots, setHiddenSlots]   = useState(new Set()); // slot numbers hidden by user
  const [myStreamHidden, setMyStreamHidden] = useState(false); // own stream hidden (still live)
  const [fsSlot, setFsSlot]             = useState(null);      // slot currently fullscreened
  const videoAreaRef                    = useRef(null);
  const { isFs, enter: enterFs, exit: exitFs } = useFullscreen(videoAreaRef);

  // Voice
  const [voiceActive, setVoiceActive]   = useState(false);
  const [voiceMuted, setVoiceMuted]     = useState(false);
  const [voiceMembers, setVoiceMembers] = useState([]);
  const [voiceStreams, setVoiceStreams] = useState({});
  const voicePeers   = useRef({});
  const voiceStream  = useRef(null);

  // peerConns: keyed by `${slot}-${viewerId}` for streamer, or `${slot}` for viewer
  const peerConns      = useRef({});
  const viewerPcs      = useRef({}); // slot → RTCPeerConnection (viewer side)
  const iceCandidateBuffer = useRef({}); // slot → candidate[] buffered before PC ready
  const socketRef      = useRef(null);
  const [liveSocket, setLiveSocket] = useState(null);
  const guests = useLiveGuestPresence({ socket: liveSocket, voiceMembers, voiceStreams,
    localStream: voiceActive ? voiceStream.current : null, localMuted: voiceMuted });
  const localStreamRef = useRef(null);
  const mySlotRef      = useRef(null);
  // Tracks last time we emitted request_offer per slot — prevents rapid duplicate
  // requests when stream_status fires repeatedly (every 5s) before the PC is ready.
  const lastOfferRequest = useRef({}); // slot → timestamp

  useEffect(() => { localStreamRef.current = localStream; }, [localStream]);
  useEffect(() => { mySlotRef.current = mySlot; }, [mySlot]);

  // ── Socket ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!displayName) return;
    const role  = isStreamer ? 'streamer' : 'viewer';
    const token = localStorage.getItem('tavern_token') || '';
    const s = io(`${SERVER}/stream`, {
      // Namespace queries belong to the underlying Manager handshake. The game
      // connects first without chat metadata; do not inherit that transport.
      forceNew: true,
      query: { name: displayName, role },
      auth: { token },
      // Prefer WebSocket so game input does not wait for a polling upgrade.
      // Socket.IO can still fall back to polling on restrictive networks.
      transports: ['websocket', 'polling'],
      tryAllTransports: true,
      upgrade: true,
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: 15,
      timeout: 30000,
    });
    socketRef.current = s;
    setLiveSocket(s);

    const mergeStatus = (st) => {
      setStatus(prev => {
        const prevSlotMap = {};
        (prev.slots || []).forEach(sl => { prevSlotMap[sl.slot] = sl; });

        const slots = (st.slots || []).map(incoming => {
          if (incoming.live && incoming.hlsUrl) {
            const incomingBase = incoming.hlsUrl.split('?')[0];
            const existing = prevSlotMap[incoming.slot];
            const existingBase = existing?.hlsUrl?.split('?')[0];
            // Only stamp a new _t when the HLS URL base path actually changes
            // (i.e. a new stream started). Re-stamping on every stream_status
            // broadcast (every 5s) changes the hlsUrl prop, which triggers
            // HlsPlayer's useEffect and tears down/restarts the HLS session.
            if (existing?.hlsUrl && existingBase === incomingBase) {
              return { ...incoming, hlsUrl: existing.hlsUrl }; // preserve existing stamped URL
            }
            return { ...incoming, hlsUrl: `${incomingBase}?_t=${Date.now()}` };
          }
          return incoming;
        });
        const source = slots[0]?.source || prev.source || 'browser';
        return { ...prev, ...st, slots, source };
      });
      if (st.title) setMySlotTitle(t => st.slots?.find(sl => sl.slot === mySlotRef.current)?.title || t);

      // For streamers-as-viewers: when we receive status showing active browser
      // slots that aren't ours, request an offer so we can watch them.
      // Rate-limited to 15s per slot — stream_status fires every 5s so without
      // this guard each broadcast triggers a new offer/answer cycle, tearing down
      // any in-progress connection before it completes.
      if (isStreamer) {
        const now = Date.now();
        (st.slots || []).forEach(slotStatus => {
          if (slotStatus.source !== 'browser') return;
          if (slotStatus.slot === mySlotRef.current) return;
          const existingPc = viewerPcs.current[slotStatus.slot];
          const pcHealthy = existingPc &&
            existingPc.connectionState !== 'failed' &&
            existingPc.connectionState !== 'closed' &&
            existingPc.signalingState  !== 'closed';
          if (pcHealthy) return; // already connected or negotiating
          const lastReq = lastOfferRequest.current[slotStatus.slot] || 0;
          if (now - lastReq < 15000) return; // cooldown — don't spam offers
          lastOfferRequest.current[slotStatus.slot] = now;
          console.log(`[live] stream_status: requesting offer for slot=${slotStatus.slot}`);
          s.emit('request_offer', { fromSlot: slotStatus.slot, viewerId: s.id });
        });
      }
    };

    s.on('stream_status', mergeStatus);
    s.on('hls_ready', ({ slot, hlsUrl }) => {
      const base = hlsUrl.split('?')[0];
      setStatus(prev => {
        const slots = prev.slots.map(sl => sl.slot === slot ? { ...sl, hlsUrl: `${base}?_t=${Date.now()}`, source: 'rtmp' } : sl);
        // If this slot isn't in list yet, add it
        if (!slots.find(sl => sl.slot === slot)) slots.push({ slot, live: true, source: 'rtmp', hlsUrl: `${base}?_t=${Date.now()}`, streamerName: 'OBS' });
        return { ...prev, slots, live: true };
      });
    });
    s.on('stream_ended', () => { setStatus({ live: false, slots: [], viewerCount: 0 }); setRemoteStreams({}); setStreaming(false); setMySlot(null); mySlotRef.current = null; lastOfferRequest.current = {}; });
    s.on('stream_offline', () => { setStatus(p => ({ ...p, live: false })); });
    s.on('chat_history', msgs => setMessages(msgs));
    s.on('chat_message', msg  => setMessages(prev => [...prev.slice(-199), msg]));
    s.on('reconnect', () => { s.emit('get_status'); });

    // ── Viewer WebRTC — ALL users receive streams for slots they don't own ──
    {
      const handleOffer = async (slot, offer) => {
        if (!offer) return;
        // Dedup guard: if a PC for this slot already exists and is mid-negotiation
        // or connected, skip this offer. Without this, a race between viewer_joined
        // and stream_status both triggering at the same time creates two concurrent
        // handleOffer calls — the second closes the first PC mid-flight.
        const priorPc = viewerPcs.current[slot];
        if (priorPc) {
          const cs = priorPc.connectionState;
          const ss = priorPc.signalingState;
          if ((cs === 'connecting' || cs === 'connected') && ss !== 'closed') {
            console.log(`[live] handleOffer slot=${slot} skipped — PC already ${cs}/${ss}`);
            return;
          }
        }
        console.log(`[live] handleOffer slot=${slot} mySlot=${mySlotRef.current} isStreamer=${isStreamer}`);
        try {
          const iceResp = await fetch(`${SERVER}/turn-credentials`).then(r => r.json())
            .catch(() => ({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] }));

          viewerPcs.current[slot]?.close();
          // Use 'all' to allow direct LAN connection between same-network streamers
          // TURN relay as fallback only — forcing relay breaks same-LAN connections
          const policy = 'all';
          const pc = new RTCPeerConnection({
            iceServers: iceResp.iceServers,
            iceTransportPolicy: policy,
          });
          viewerPcs.current[slot] = pc;
          pc.ontrack = ({ streams }) => {
            console.log(`[live] ontrack slot=${slot} streams=${streams.length}`);
            if (streams[0]) setRemoteStreams(prev => ({ ...prev, [slot]: streams[0] }));
          };
          pc.onicecandidate = ({ candidate }) => {
            if (candidate) {
              console.log(`[live] viewer_ice out slot=${slot} type=${candidate.type}`);
              s.emit('viewer_ice', { candidate: candidate.toJSON(), slot });
            } else {
              console.log(`[live] ICE gathering done slot=${slot}`);
            }
          };
          pc.oniceconnectionstatechange = () => console.log(`[live] ICE conn slot=${slot} state=${pc.iceConnectionState}`);
          pc.onconnectionstatechange = () => {
            console.log(`[live] pc slot=${slot} state=${pc.connectionState} policy=${policy}`);
            if (['disconnected','failed'].includes(pc.connectionState)) {
              setRemoteStreams(prev => { const n = { ...prev }; delete n[slot]; return n; });
              // Clear cooldown so mergeStatus can request a fresh offer on next status tick
              delete lastOfferRequest.current[slot];
            }
          };
          pc.addTransceiver('video', { direction: 'recvonly' });
          pc.addTransceiver('audio', { direction: 'recvonly' });
          await pc.setRemoteDescription({ type: 'offer', sdp: offer });
          // Flush any ICE candidates that arrived before PC was ready
          const buffered = iceCandidateBuffer.current[slot] || [];
          if (buffered.length) {
            console.log(`[live] flushing ${buffered.length} buffered ICE candidates slot=${slot}`);
            for (const c of buffered) { try { await pc.addIceCandidate(c); } catch {} }
            iceCandidateBuffer.current[slot] = [];
          }
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          console.log(`[live] handleOffer DONE slot=${slot} — sending viewer_answer`);
          s.emit('viewer_answer', { answer: answer.sdp, slot });
        } catch (e) { console.error(`[live] handleOffer ERROR slot=${slot}:`, e); }
      };

      // Relay-only retry for when direct P2P fails (same-NAT scenarios)
      const handleOfferRelay = async (slot, offer) => {
        if (!offer) return;
        console.log(`[live] handleOfferRelay (TURN relay only) slot=${slot}`);
        try {
          const iceResp = await fetch(`${SERVER}/turn-credentials`).then(r => r.json())
            .catch(() => ({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] }));
          viewerPcs.current[slot]?.close();
          const pc = new RTCPeerConnection({ iceServers: iceResp.iceServers, iceTransportPolicy: 'relay' });
          viewerPcs.current[slot] = pc;
          pc.ontrack = ({ streams }) => {
            console.log(`[live] ontrack (relay) slot=${slot}`);
            if (streams[0]) setRemoteStreams(prev => ({ ...prev, [slot]: streams[0] }));
          };
          pc.onicecandidate = ({ candidate }) => { if (candidate) s.emit('viewer_ice', { candidate: candidate.toJSON(), slot }); };
          pc.onconnectionstatechange = () => {
            console.log(`[live] pc relay slot=${slot} state=${pc.connectionState}`);
            if (['disconnected','failed'].includes(pc.connectionState)) {
              setRemoteStreams(prev => { const n = { ...prev }; delete n[slot]; return n; });
            }
          };
          pc.addTransceiver('video', { direction: 'recvonly' });
          pc.addTransceiver('audio', { direction: 'recvonly' });
          await pc.setRemoteDescription({ type: 'offer', sdp: offer });
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          s.emit('viewer_answer', { answer: answer.sdp, slot });
        } catch (e) { console.error(`[live] handleOfferRelay ERROR slot=${slot}:`, e); }
      };
      s.on('stream_offer',  ({ offer, slot = 1 })  => handleOffer(slot, offer));
      s.on('stream_answer', ({ answer, slot = 1 }) => handleOffer(slot, answer));
      s.on('viewer_ice_from_streamer', async ({ candidate, slot = 1 }) => {
        console.log(`[live] viewer_ice_from_streamer slot=${slot} type=${candidate?.type} pc exists=${!!viewerPcs.current[slot]}`);
        const pc = viewerPcs.current[slot];
        if (pc && pc.remoteDescription) {
          try { await pc.addIceCandidate(candidate); } catch(e) { console.warn('[live] ice_from_streamer error:', e); }
        } else {
          // Buffer candidate until PC is ready
          if (!iceCandidateBuffer.current[slot]) iceCandidateBuffer.current[slot] = [];
          iceCandidateBuffer.current[slot].push(candidate);
          console.log(`[live] buffered ICE candidate for slot=${slot}`);
        }
      });
      s.on('stream_offline', () => { setStatus({ live: false, slots: [], viewerCount: 0 }); setRemoteStreams({}); });
    }

    // ── Streamer WebRTC ────────────────────────────────────────────────────
    if (isStreamer) {
      const createOffer = async (viewerId, slot) => {
        // Wait up to 3s for local stream to be available (race with startStreaming)
        let stream = localStreamRef.current;
        if (!stream) {
          for (let i = 0; i < 6; i++) {
            await new Promise(r => setTimeout(r, 500));
            stream = localStreamRef.current;
            if (stream) break;
          }
        }
        if (!stream || !slot) {
          console.warn('[live] createOffer — no stream or slot', { slot, hasStream: !!stream, mySlot: mySlotRef.current });
          return;
        }
        const key = `${slot}-${viewerId}`;
        // Dedup: if we already have a healthy PC for this viewer don't tear it
        // down and restart. Duplicate viewer_joined events (from cross-slot
        // notifications on streamer_offer + initial connect) would otherwise
        // clobber an in-progress or established connection.
        const existingPc = peerConns.current[key];
        if (existingPc) {
          const ss = existingPc.signalingState;
          const cs = existingPc.connectionState;
          if ((ss === 'have-local-offer' || ss === 'stable') && cs !== 'failed' && cs !== 'closed') {
            console.log(`[live] createOffer skipped — already active signalingState=${ss} connectionState=${cs} viewer=${viewerId}`);
            return;
          }
        }
        console.log(`[live] createOffer for viewer ${viewerId} slot ${slot}`);
        try {
          const iceResp = await fetch(`${SERVER}/turn-credentials`).then(r => r.json())
            .catch(() => ({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] }));
          peerConns.current[key]?.close();
          const pc = new RTCPeerConnection({ iceServers: iceResp.iceServers });
          peerConns.current[key] = pc;
          stream.getTracks().forEach(t => pc.addTrack(t, stream));
          pc.onicecandidate = ({ candidate }) => {
            if (candidate) s.emit('streamer_ice', { to: viewerId, candidate: candidate.toJSON(), slot });
          };
          pc.onconnectionstatechange = () => {
            if (['disconnected','failed','closed'].includes(pc.connectionState)) { pc.close(); delete peerConns.current[key]; }
          };
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          s.emit('answer_for_viewer', { to: viewerId, answer: offer.sdp, slot });
        } catch (e) { console.error('[live] offer error:', e); }
      };
      s.on('viewer_joined', ({ viewerId, slot }) => {
        console.log(`[live] viewer_joined received: viewer=${viewerId} slot=${slot} mySlot=${mySlotRef.current} hasStream=${!!localStreamRef.current}`);
        if (!slot) { console.warn('[live] viewer_joined with no slot'); return; }
        if (slot === mySlotRef.current) {
          // A viewer joined OUR stream — create offer for them
          createOffer(viewerId, slot);
        } else {
          // We want to watch another slot — request that streamer sends us an offer
          console.log(`[live] requesting offer from slot ${slot} (we are viewer of it)`);
          s.emit('request_offer', { fromSlot: slot, viewerId: s.id });
        }
      });
      s.on('viewer_answer', async ({ from, answer, slot }) => {
        const targetSlot = slot || mySlotRef.current;
        const key = `${targetSlot}-${from}`;
        console.log(`[live] viewer_answer from=${from} slot=${targetSlot} key=${key} pc exists=${!!peerConns.current[key]}`);
        const pc = peerConns.current[key];
        if (pc?.signalingState === 'have-local-offer') {
          try { await pc.setRemoteDescription({ type: 'answer', sdp: answer }); console.log(`[live] viewer_answer applied slot=${targetSlot}`); } catch(e) { console.error('[live] viewer_answer error:', e); }
        } else {
          console.warn(`[live] viewer_answer ignored — pc state=${pc?.signalingState} key=${key}`);
        }
      });
      s.on('viewer_ice', async ({ from, candidate, slot }) => {
        const targetSlot = slot || mySlotRef.current;
        const key = `${targetSlot}-${from}`;
        console.log(`[live] viewer_ice in slot=${targetSlot} mySlot=${mySlotRef.current} from=${from} type=${candidate?.type}`);
        if (targetSlot !== mySlotRef.current && viewerPcs.current[targetSlot]) {
          try { await viewerPcs.current[targetSlot]?.addIceCandidate(candidate); } catch(e) { console.warn('[live] viewerPcs ice error:', e); }
        } else {
          try { await peerConns.current[key]?.addIceCandidate(candidate); } catch(e) { console.warn('[live] peerConns ice error:', e); }
        }
      });
      s.on('viewer_left', ({ viewerId, slot }) => {
        const targetSlot = slot || mySlotRef.current;
        const key = `${targetSlot}-${viewerId}`;
        peerConns.current[key]?.close(); delete peerConns.current[key];
      });
    }

    // ── Voice ──────────────────────────────────────────────────────────────
    const makeVoicePeer = async (peerId, initiate) => {
      const iceResp = await fetch(`${SERVER}/turn-credentials`).then(r => r.json())
        .catch(() => ({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] }));
      const pc = new RTCPeerConnection({ iceServers: iceResp.iceServers });
      voicePeers.current[peerId] = { pc };
      voiceStream.current?.getTracks().forEach(t => pc.addTrack(t, voiceStream.current));
      pc.ontrack = ({ streams }) => { if (streams[0]) setVoiceStreams(prev => ({ ...prev, [peerId]: streams[0] })); };
      pc.onicecandidate = ({ candidate }) => { if (candidate) s.emit('voice_ice', { to: peerId, candidate }); };
      pc.onconnectionstatechange = () => {
        if (['disconnected','failed','closed'].includes(pc.connectionState)) {
          pc.close(); delete voicePeers.current[peerId];
          setVoiceStreams(prev => { const n = { ...prev }; delete n[peerId]; return n; });
        }
      };
      if (initiate) { const offer = await pc.createOffer(); await pc.setLocalDescription(offer); s.emit('voice_offer', { to: peerId, offer: offer.sdp }); }
      return pc;
    };
    s.on('voice_members', ({ members }) => setVoiceMembers(members));
    s.on('voice_offer', async ({ from, offer }) => { const pc = await makeVoicePeer(from, false); await pc.setRemoteDescription({ type: 'offer', sdp: offer }); const ans = await pc.createAnswer(); await pc.setLocalDescription(ans); s.emit('voice_answer', { to: from, answer: ans.sdp }); });
    s.on('voice_answer', async ({ from, answer }) => { const p = voicePeers.current[from]; if (p?.pc?.signalingState === 'have-local-offer') await p.pc.setRemoteDescription({ type: 'answer', sdp: answer }).catch(() => {}); });
    s.on('voice_ice', async ({ from, candidate }) => { try { await voicePeers.current[from]?.pc?.addIceCandidate(candidate); } catch {} });
    s.on('voice_peer_joined', async ({ peerId }) => { await makeVoicePeer(peerId, true); });
    s.on('voice_peer_left', ({ peerId }) => { voicePeers.current[peerId]?.pc?.close(); delete voicePeers.current[peerId]; setVoiceStreams(prev => { const n = { ...prev }; delete n[peerId]; return n; }); });

    s.emit('get_status');

    return () => {
      s.disconnect();
      setLiveSocket(null);
      Object.values(viewerPcs.current).forEach(pc => pc?.close());
      Object.values(peerConns.current).forEach(pc => pc.close());
      Object.values(voicePeers.current).forEach(({ pc }) => pc?.close());
      voiceStream.current?.getTracks().forEach(t => t.stop());
      peerConns.current = {}; viewerPcs.current = {}; voicePeers.current = {};
    };
  }, [displayName]); // eslint-disable-line

  const joinVoice = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      voiceStream.current = stream; setVoiceActive(true);
      socketRef.current?.emit('voice_join', { name: displayName });
    } catch (e) { alert('Microphone access denied: ' + e.message); }
  }, [displayName]);

  const leaveVoice = useCallback(() => {
    voiceStream.current?.getTracks().forEach(t => t.stop()); voiceStream.current = null;
    Object.values(voicePeers.current).forEach(({ pc }) => pc?.close()); voicePeers.current = {};
    setVoiceStreams({}); setVoiceActive(false); setVoiceMuted(false);
    socketRef.current?.emit('voice_leave');
  }, []);

  const toggleVoiceMute = useCallback(() => {
    const tracks = voiceStream.current?.getAudioTracks();
    if (!tracks?.length) return;
    const next = !tracks[0].enabled; tracks.forEach(t => { t.enabled = next; }); setVoiceMuted(!next);
  }, []);

  const startStreaming = useCallback(async (stream) => {
    if (!socketRef.current) return;
    setLocalStream(stream); localStreamRef.current = stream;
    const token = localStorage.getItem('tavern_token') || '';
    try {
      const resp = await fetch(`${SERVER}/stream/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: mySlotTitle }),
      });
      const data = await resp.json();
      const slot = data.slot;
      setMySlot(slot); mySlotRef.current = slot;
      socketRef.current.emit('claim_slot', { slot });
      socketRef.current.emit('streamer_offer', { offer: null, slot });
    } catch (e) { console.error('start stream error:', e); }
    setStreaming(true);
  }, [mySlotTitle]);

  const endStreaming = useCallback(() => {
    fetch(`${SERVER}/stream/end`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('tavern_token') || ''}` } }).catch(() => {});
    socketRef.current?.emit('end_stream', { slot: mySlot });
    localStream?.getTracks().forEach(t => t.stop());
    setLocalStream(null); localStreamRef.current = null;
    setStreaming(false); setMySlot(null); mySlotRef.current = null;
    Object.keys(peerConns.current).filter(k => k.startsWith(`${mySlot}-`)).forEach(k => { peerConns.current[k]?.close(); delete peerConns.current[k]; });
  }, [localStream, mySlot]);

  const handleTitleChange = useCallback((t) => {
    setMySlotTitle(t);
    socketRef.current?.emit('stream_title', { title: t, slot: mySlot });
  }, [mySlot]);

  // Called by StreamerControls when the user flips the camera.
  // Replaces the video track on every active peer connection so viewers
  // don't lose video — without this their WebRTC PC detects a missing
  // track and disconnects/fails.
  const handleCameraFlip = useCallback(async (newTrack) => {
    const pcs = peerConns.current;
    await Promise.all(
      Object.values(pcs).map(async pc => {
        try {
          const sender = pc.getSenders().find(s => s.track?.kind === 'video');
          if (sender) {
            await sender.replaceTrack(newTrack);
            console.log('[live] replaceTrack done on peer connection');
          }
        } catch (e) { console.warn('[live] replaceTrack error:', e); }
      })
    );
  }, []);

  const sendChat = useCallback((content) => { socketRef.current?.emit('chat_message', { content }); }, []);

  const voiceAudioEls = useMemo(() => (
    Object.entries(voiceStreams).map(([peerId, stream]) => (
      <audio key={peerId} autoPlay ref={el => { if (el && el.srcObject !== stream) { el.srcObject = stream; el.play().catch(() => {}); } }}/>
    ))
  ), [voiceStreams]);

  const voiceChatPanel = useMemo(() => (voiceActive || voiceMembers.length > 0) && (
    <div style={{ background: C.card, borderBottom: `1px solid ${C.border}`, padding: '12px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: voiceActive && voiceMembers.length > 0 ? 10 : 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, flex: 1 }}>🎙 Voice Chat</div>
      </div>
      {voiceMembers.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {voiceMembers.map((m, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, background: C.border, fontSize: 12 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22C55E' }}/>
              <span style={{ color: m.role === 'streamer' ? C.gold : C.text }}>{m.name}</span>
            </div>
          ))}
        </div>
      )}
      {voiceActive && voiceMembers.length <= 1 && <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>You're in voice — waiting for others...</div>}
    </div>
  ), [voiceActive, voiceMembers]);

  // Sync fsSlot when fullscreen exits (e.g. user presses Escape).
  // MUST be before the early return below — hooks cannot be called after
  // a conditional return (Rules of Hooks), or React throws error #310.
  useEffect(() => {
    if (!isFs && fsSlot !== null) setFsSlot(null);
  }, [isFs, fsSlot]);

  if (!displayName) return <NameGate onEnter={name => { try { sessionStorage.setItem('tavern-live-guest-name', name); } catch {} setDisplayName(name); }}/>;

  const activeSlots = status.slots || [];
  const totalActive = activeSlots.length;

  const headerTitle = totalActive > 0
    ? activeSlots.map(s => s.streamerName || s.title).join(' vs ')
    : 'Live Stream';

  // Slots visible to this user (respecting hide + myStreamHidden)
  const visibleSlots = activeSlots.filter(sl => {
    if (hiddenSlots.has(sl.slot)) return false;
    if (sl.slot === mySlot && myStreamHidden) return false;
    return true;
  });

  const effectiveLayout = visibleSlots.length <= 1 ? 'equal' : layout;
  const spotlit = spotlightSlot ?? visibleSlots[0]?.slot;

  // ── Per-slot fullscreen handler ──────────────────────────────────────────
  const handleFullscreen = (slot) => {
    if (isFs) { exitFs(); setFsSlot(null); }
    else { setFsSlot(slot); enterFs(); }
  };

  // ── Layout CSS grid/flex builder ─────────────────────────────────────────
  const videoAreaH = isMobile ? 'auto' : 'calc(100vh - 52px - 180px)';

  const renderSlot = (slotStatus, idx, overrideStyle = {}, isPip = false) => {
    const isMySlot = slotStatus.slot === mySlot;
    const remoteStream = remoteStreams[slotStatus.slot];
    const isFocused = fsSlot === slotStatus.slot;
    return (
      <div key={slotStatus.slot} style={{ position: 'relative', overflow: 'hidden', background: '#000', ...overrideStyle }}>
        <StreamPanel
          slotStatus={slotStatus}
          remoteStream={remoteStream}
          localStream={localStream}
          isMySlot={isMySlot}
          slotIndex={activeSlots.findIndex(s => s.slot === slotStatus.slot)}
          totalSlots={totalActive}
          showControls={true}
          isPip={isPip}
          isFullscreen={isFocused && isFs}
          onHide={!isPip ? () => {
            if (isMySlot) { setMyStreamHidden(true); }
            else { setHiddenSlots(prev => new Set([...prev, slotStatus.slot])); }
          } : null}
          onFullscreen={() => handleFullscreen(slotStatus.slot)}
        />
      </div>
    );
  };

  // ── Build the video area based on layout ─────────────────────────────────
  let videoArea;

  if (totalActive === 0) {
    videoArea = (
      <div style={{ height: isMobile ? '56vw' : videoAreaH, background: '#000' }}>
        <OfflinePlaceholder/>
      </div>
    );
  } else if (effectiveLayout === 'spotlight') {
    const main = visibleSlots.find(s => s.slot === spotlit) || visibleSlots[0];
    const rest = visibleSlots.filter(s => s.slot !== main?.slot);
    videoArea = (
      <div style={{ height: isMobile ? 'auto' : videoAreaH, display: 'flex', flexDirection: isMobile ? 'column' : 'row', background: '#000' }}>
        <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {main && renderSlot(main, 0, { width: '100%', height: isMobile ? '56vw' : '100%' })}
        </div>
        {rest.length > 0 && (
          <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', width: isMobile ? '100%' : 160, flexShrink: 0, borderLeft: isMobile ? 'none' : `1px solid ${C.border}`, borderTop: isMobile ? `1px solid ${C.border}` : 'none' }}>
            {rest.map((sl, i) => (
              <div key={sl.slot} onClick={() => setSpotlightSlot(sl.slot)}
                style={{ flex: 1, cursor: 'pointer', opacity: 0.85, transition: 'opacity .15s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = 1}
                onMouseLeave={e => e.currentTarget.style.opacity = .85}>
                {renderSlot(sl, i + 1, { width: '100%', height: isMobile ? '28vw' : '100%' })}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  } else if (effectiveLayout === 'focus' && visibleSlots.length > 1) {
    const main = visibleSlots[0];
    const rest = visibleSlots.slice(1);
    videoArea = (
      <div style={{ height: isMobile ? 'auto' : videoAreaH, display: 'flex', flexDirection: isMobile ? 'column' : 'row', background: '#000' }}>
        <div style={{ flex: '0 0 65%', minWidth: 0, minHeight: 0 }}>
          {renderSlot(main, 0, { width: '100%', height: isMobile ? '50vw' : '100%' })}
        </div>
        <div style={{ flex: '0 0 35%', display: 'flex', flexDirection: isMobile ? 'row' : 'column', borderLeft: isMobile ? 'none' : `1px solid ${C.border}`, borderTop: isMobile ? `1px solid ${C.border}` : 'none' }}>
          {rest.map((sl, i) => (
            <div key={sl.slot} style={{ flex: 1, borderTop: !isMobile && i > 0 ? `1px solid ${C.border}` : 'none', borderLeft: isMobile && i > 0 ? `1px solid ${C.border}` : 'none' }}>
              {renderSlot(sl, i + 1, { width: '100%', height: isMobile ? '50vw' : '100%' })}
            </div>
          ))}
        </div>
      </div>
    );
  } else if (effectiveLayout === 'grid' && visibleSlots.length > 1) {
    const cols = visibleSlots.length <= 2 ? 2 : Math.ceil(Math.sqrt(visibleSlots.length));
    const rows = Math.ceil(visibleSlots.length / cols);
    const cellH = isMobile ? `${Math.round(56 / rows)}vw` : `calc(${videoAreaH} / ${rows})`;
    videoArea = (
      <div style={{ height: isMobile ? 'auto' : videoAreaH, display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, background: '#000', gap: 2 }}>
        {visibleSlots.map((sl, i) => renderSlot(sl, i, { width: '100%', height: cellH }))}
      </div>
    );
  } else if (effectiveLayout === 'pip' && mySlot && visibleSlots.length > 1) {
    // Own stream as small PiP overlay, others fill the area
    const others = visibleSlots.filter(s => s.slot !== mySlot);
    const mine   = visibleSlots.find(s => s.slot === mySlot);
    const cols = others.length === 1 ? 1 : 2;
    const cellH = isMobile ? '56vw' : videoAreaH;
    videoArea = (
      <div style={{ position: 'relative', height: isMobile ? 'auto' : videoAreaH, background: '#000' }}>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, height: '100%', gap: 2 }}>
          {others.map((sl, i) => renderSlot(sl, i, { width: '100%', height: isMobile ? cellH : '100%' }))}
        </div>
        {/* PiP overlay for own stream */}
        {mine && !myStreamHidden && (
          <div style={{ position: 'absolute', bottom: 16, right: 16, width: 200, height: 113, borderRadius: 10, overflow: 'hidden', border: `2px solid ${C.gold}`, boxShadow: '0 4px 20px rgba(0,0,0,0.8)', cursor: 'move', zIndex: 10 }}>
            {renderSlot(mine, visibleSlots.length - 1, { width: '100%', height: '100%' }, true)}
          </div>
        )}
      </div>
    );
  } else {
    // Equal / auto — side by side or single
    videoArea = (
      <div style={{ height: isMobile ? 'auto' : videoAreaH, display: 'flex', flexDirection: isMobile ? 'column' : 'row', background: '#000' }}>
        {visibleSlots.map((sl, i) => (
          <div key={sl.slot} style={{ flex: 1, borderRight: !isMobile && i < visibleSlots.length - 1 ? `2px solid ${C.border}` : 'none', borderBottom: isMobile && i < visibleSlots.length - 1 ? `2px solid ${C.border}` : 'none', minWidth: 0, minHeight: 0 }}>
            {renderSlot(sl, i, { width: '100%', height: isMobile ? `${Math.round(56 / visibleSlots.length)}vw` : '100%' })}
          </div>
        ))}
      </div>
    );
  }

  // ── Layout toolbar ───────────────────────────────────────────────────────
  const layoutToolbar = totalActive > 0 && !isMobile && (
    <div style={{ background: C.sidebar, borderBottom: `1px solid ${C.border}`, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
      {/* Layout presets */}
      <span style={{ fontSize: 11, color: C.muted, marginRight: 4, fontWeight: 600 }}>Layout:</span>
      {LAYOUT_OPTS.map(opt => (
        <button key={opt.id} onClick={() => setLayout(opt.id)} title={opt.tip}
          style={{ padding: '4px 10px', borderRadius: 7, border: `1px solid ${layout === opt.id ? C.gold : C.border}`, background: layout === opt.id ? `${C.gold}22` : 'transparent', color: layout === opt.id ? C.gold : C.muted, cursor: 'pointer', fontSize: 11, fontWeight: layout === opt.id ? 700 : 400 }}>
          {opt.label}
        </button>
      ))}

      {/* Spotlight slot selector */}
      {layout === 'spotlight' && visibleSlots.length > 1 && (
        <>
          <span style={{ fontSize: 11, color: C.muted, marginLeft: 8, marginRight: 4 }}>Focus:</span>
          {visibleSlots.map((sl, i) => (
            <button key={sl.slot} onClick={() => setSpotlightSlot(sl.slot)}
              style={{ padding: '4px 10px', borderRadius: 7, border: `1px solid ${spotlit === sl.slot ? SLOT_COLORS[i] : C.border}`, background: spotlit === sl.slot ? SLOT_COLORS[i] + '33' : 'transparent', color: spotlit === sl.slot ? SLOT_COLORS[i] : C.muted, cursor: 'pointer', fontSize: 11, fontWeight: spotlit === sl.slot ? 700 : 400 }}>
              {sl.streamerName}
            </button>
          ))}
        </>
      )}

      <div style={{ flex: 1 }}/>

      {/* Hidden stream badges + restore buttons */}
      {[...hiddenSlots].map(slot => {
        const sl = activeSlots.find(s => s.slot === slot);
        if (!sl) return null;
        const idx = activeSlots.findIndex(s => s.slot === slot);
        return (
          <button key={slot} onClick={() => setHiddenSlots(prev => { const n = new Set(prev); n.delete(slot); return n; })}
            style={{ padding: '4px 10px', borderRadius: 7, border: `1px solid ${SLOT_COLORS[idx]}`, background: SLOT_COLORS[idx] + '22', color: SLOT_COLORS[idx], cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
            👁 {sl.streamerName}
          </button>
        );
      })}

      {/* Own stream hidden indicator */}
      {myStreamHidden && streaming && (
        <button onClick={() => setMyStreamHidden(false)}
          style={{ padding: '4px 10px', borderRadius: 7, border: `1px solid ${C.gold}`, background: `${C.gold}22`, color: C.gold, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
          🔴 LIVE · Show My Stream
        </button>
      )}
    </div>
  );

  // Mobile hidden stream notification
  const mobileHiddenBadge = isMobile && myStreamHidden && streaming && (
    <div style={{ background: `${C.gold}22`, border: `1px solid ${C.gold}`, borderRadius: 8, margin: '8px 12px', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.live, flexShrink: 0 }}/>
      <div style={{ fontSize: 12, color: C.gold, fontWeight: 700, flex: 1 }}>Stream is live (your preview is hidden)</div>
      <button onClick={() => setMyStreamHidden(false)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: `1px solid ${C.gold}`, background: 'transparent', color: C.gold, cursor: 'pointer' }}>Show</button>
    </div>
  );

  const changeMode = mode => {
    setViewMode(mode);
    const nextUrl = new URL(window.location.href);
    if (mode === 'play') { nextUrl.searchParams.set('play', '1'); nextUrl.searchParams.delete('watch'); }
    else { nextUrl.searchParams.delete('play'); nextUrl.searchParams.delete('netplay'); nextUrl.searchParams.set('watch', '1'); }
    window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}`);
  };

  return (
    <div style={{ height: '100dvh', overflow: 'hidden', background: C.bg, fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif', color: C.text, display: 'flex', flexDirection: 'column' }}>
      {voiceAudioEls}

      {/* Top bar */}
      <header aria-label="Live room toolbar" style={{ background: C.sidebar, borderBottom: `1px solid ${C.border}`, padding: isMobile ? '8px 12px' : '0 20px', display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', alignItems: 'center', gap: isMobile ? 8 : 12, height: isMobile ? 'auto' : 52, minHeight: 52, boxSizing: 'border-box', flexShrink: 0 }}>
        <div style={{ fontSize: 20 }}>🍺</div>
        <div style={{ fontWeight: 800, fontSize: 16, color: C.gold, flexShrink: 0 }}>Tavern Live</div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {[['watch', 'Watch'], ['play', '🎮 Play']].map(([mode, label]) => (
            <button key={mode} onClick={() => changeMode(mode)} style={{ padding: '6px 10px', borderRadius: 7, border: `1px solid ${viewMode === mode ? C.gold : C.border}`, background: viewMode === mode ? `${C.gold}22` : 'transparent', color: viewMode === mode ? C.gold : C.muted, cursor: 'pointer', fontSize: 12, fontWeight: viewMode === mode ? 800 : 500 }}>{label}</button>
          ))}
        </div>
        {totalActive > 0 ? (
          <>
            <div style={{ background: C.live, color: '#fff', fontWeight: 800, fontSize: 10, padding: '2px 8px', borderRadius: 4, letterSpacing: 1, flexShrink: 0 }}>LIVE</div>
            {totalActive > 1 && activeSlots.map((sl, i) => (
              <div key={sl.slot} style={{ background: SLOT_COLORS[i % SLOT_COLORS.length] + '33', color: SLOT_COLORS[i % SLOT_COLORS.length], fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 4, flexShrink: 0 }}>
                {sl.streamerName}
              </div>
            ))}
            <div style={{ color: C.text, fontSize: 14, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{headerTitle}</div>
            <div style={{ color: C.muted, fontSize: 13, flexShrink: 0 }}>👁 {status.viewerCount || 0}</div>
          </>
        ) : (
          <div style={{ color: C.muted, fontSize: 14, flex: 1 }}>Offline</div>
        )}
        <LiveVoiceControls active={voiceActive} muted={voiceMuted} isMobile={isMobile} onJoin={joinVoice} onMute={toggleVoiceMute} onLeave={leaveVoice}/>
        <a href="/" style={{ color: C.muted, fontSize: 13, textDecoration: 'none', flexShrink: 0 }}>← Tavern</a>
      </header>

      {viewMode === 'watch' && layoutToolbar}

      {/* Body */}
      {viewMode === 'play' ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: isMobile ? 'auto' : 'hidden', minHeight: 0 }}>
          <div style={{ flex: isMobile ? '0 0 auto' : 1, overflow: isMobile ? 'visible' : 'auto', minWidth: 0 }}>
            {!localNetplay && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: 12, fontSize: 13 }}><a href="/live?netplay=1" style={{ color: C.gold }}>Local netplay</a><a href="/live?play=1&games=streaming" style={{ color: C.gold }}>Streaming games</a></div>}
            {nativeLinked ? <GbaLinkedPage displayName={displayName} sidebarExtras={<nav aria-label="Game views"><a href="/live?netplay=1">Local netplay</a><a href="/live?play=1&games=streaming">Streaming games</a></nav>}/> : localNetplay ? <>
              <div style={{ display: 'grid', gridTemplateColumns: dualGames && !isMobile ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)', gap: 8 }}>
                <NetplayRoom displayName={displayName} keyboardActive={activeGame === 0} onSelectControls={dualGames ? () => setActiveGame(0) : undefined} sidebarExtras={<>
                  <nav aria-label="Game views"><a href="/live?netplay=1">Local netplay</a><a href="/live?play=1&games=gba-link">Four Swords · server link</a><a href="/live?play=1&games=streaming">Streaming games</a></nav>
                  <button onClick={() => { const next = !dualGames; setDualGames(next); if (!next) setActiveGame(0); const url = new URL(window.location.href); next ? url.searchParams.set('screens', '2') : url.searchParams.delete('screens'); window.history.replaceState({}, '', url); }}>{dualGames ? 'Show one game' : 'Show two independent games'}</button>
                  {dualGames && <p>Separate games and saves. Assign each person Player 1 in their game, and choose where keyboard/gamepad controls apply.</p>}
                </>}/>
                {dualGames && <NetplayRoom displayName={displayName} basePath="/netplay-side" keyboardActive={activeGame === 1} onSelectControls={() => setActiveGame(1)}/>}
              </div>
            </> : <GameRoom socket={liveSocket} displayName={displayName} isMobile={isMobile}/>}
            {voiceChatPanel}
          </div>
          {!isMobile && (
            <div style={{ width: 340, flexShrink: 0, borderLeft: `1px solid ${C.border}` }}>
              <Chat messages={messages} onSend={sendChat} displayName={displayName} guests={guests}/>
            </div>
          )}
          {isMobile && (
            <div style={{ minHeight: 280, borderTop: `1px solid ${C.border}` }}>
              <Chat messages={messages} onSend={sendChat} displayName={displayName} guests={guests}/>
            </div>
          )}
        </div>
      ) : (
      <div style={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: 'hidden', minHeight: 0 }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', minWidth: 0 }}>

          {/* Video area — ref'd for fullscreen API */}
          <div ref={videoAreaRef} style={{ background: '#000', flexShrink: 0 }}>
            {videoArea}
          </div>

          {mobileHiddenBadge}

          {/* Stream info bar */}
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
            {totalActive === 0
              ? <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>No streams active</div>
              : totalActive === 1
                ? <>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 2 }}>{activeSlots[0].title}</div>
                    <div style={{ color: C.muted, fontSize: 13 }}>Streamed by {activeSlots[0].streamerName}</div>
                  </>
                : <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {activeSlots.map((sl, i) => (
                      <div key={sl.slot} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: SLOT_COLORS[i % SLOT_COLORS.length], flexShrink: 0 }}/>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{sl.streamerName}</div>
                          <div style={{ fontSize: 12, color: C.muted }}>{sl.title}</div>
                        </div>
                      </div>
                    ))}
                  </div>
            }
          </div>

          {voiceChatPanel}
          {isStreamer && (
            <div style={{ padding: 16, flexShrink: 0 }}>
              <StreamerControls
                onStream={startStreaming} onEnd={endStreaming}
                streaming={streaming} localStream={localStream}
                mySlot={mySlot} slotTitle={mySlotTitle} onTitleChange={handleTitleChange}
                onCameraFlip={handleCameraFlip}
              />
            </div>
          )}
          {isMobile && (
            <div style={{ flex: 1, minHeight: 260, borderTop: `1px solid ${C.border}` }}>
              <Chat messages={messages} onSend={sendChat} displayName={displayName} guests={guests}/>
            </div>
          )}
        </div>
        {!isMobile && (
          <div style={{ width: 340, flexShrink: 0, borderLeft: `1px solid ${C.border}` }}>
            <Chat messages={messages} onSend={sendChat} displayName={displayName} guests={guests}/>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
